import { MovieContentCard } from '@/entities/content';
import { useHorizontalScroll } from '@/features/horizontal-scroll';
import { Content, ContentGroup } from '@/types';
import { FOCUS_ROW_ATTRIBUTE, isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { useCallback, useMemo, useRef } from 'react';

export type MovieContentListProps = {
  sourcePage: string;
  contentGroup: ContentGroup;
  contents: Array<Content>;
  onFocused?: ({ x, top }: { x: number; top: number }) => void;
};

export const MovieContentList = ({ sourcePage, contentGroup, contents, onFocused }: MovieContentListProps) => {
  // ! 줄 단위 포커스 컨테이너를 두지 않는다.
  // ! 컨테이너가 있으면 줄마다 마지막 포커스를 기억해서, 아래 키를 눌렀을 때 바로 밑의 카드가 아니라
  // ! 그 줄에서 마지막에 봤던 카드로 이동한다. 모든 카드를 형제로 두면 norigin 의 좌표 기반 탐색이
  // ! 바로 밑의 카드를 골라준다. (가로 스크롤은 focus 컨테이너와 무관하게 아래 ref 로 처리한다)
  const ref = useRef<HTMLDivElement>(null);

  useHorizontalScroll({ scrollingRef: ref });

  const onContentCardFocused = useCallback(
    ({ x, top }: { x: number; top: number }) => {
      // 가로 스크롤만 이 컴포넌트 책임. 세로 스크롤은 아래 onFocused(부모)가 자체적으로 판단한다.
      if (!isFocusScrollSuppressed('horizontal')) {
        const current = ref.current;

        if (current) {
          scrollElementTo({ element: current, left: x - 100 });
        }
      }

      if (onFocused) {
        onFocused({ x, top });
      }
    },
    [onFocused, ref],
  );

  const components = useMemo(() => {
    return contents
      .filter((content) => content.type === 'VIDEO')
      .map((content, i) => {
        const focusKey = `content-${content.id}`;

        return (
          <MovieContentCard
            sourcePage={sourcePage}
            tabIndex={i}
            key={content.id}
            content={content}
            focusKey={focusKey}
            onFocused={onContentCardFocused}
            topicId={contentGroup?.id}
            weight={i}
          />
        );
      });
  }, [contentGroup?.id, contents, onContentCardFocused, sourcePage]);

  return (
    <div className="mb-[16px]">
      {/* Header */}
      <h3 className="px-2 font-bold text-[32px] ml-[48px] text-white">{contentGroup.title}</h3>

      {/* Contents */}
      {components.length > 0 && (
        <div className="overflow-x-scroll" ref={ref} {...{ [FOCUS_ROW_ATTRIBUTE]: '' }}>
          <div className="flex items-center py-4 w-fit px-[60px]">{components}</div>
        </div>
      )}
    </div>
  );
};
